import {
  Body1,
  Button,
  Field,
  Input,
  Textarea,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAgentMutations } from '../../../hooks/useAgentMutations/useAgentMutations.js';
import type { Agent, AgentHttpProxySpec } from '../../../types/agent.js';
import type { AgentEditControls } from '../AgentForm/AgentForm.js';
import { useStyles } from '../AgentForm/AgentForm.style.js';

import { isSensitiveHeader } from './AgentSpecDetails.js';

interface HeaderRow {
  name: string;
  value: string;
}

export interface HttpAgentFormProps {
  agent: Agent;
  spec: AgentHttpProxySpec;
  workspaceName: string;
  onEditSaved: () => void;
  onEditControlsChange: (controls: AgentEditControls | null) => void;
}

function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function isHttpBaseUrl(value: string): boolean {
  if (/\s/.test(value)) return false;
  try {
    const url = new URL(value);
    return (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      url.hostname.length > 0
    );
  } catch {
    return false;
  }
}

export function HttpAgentForm({
  agent,
  spec,
  workspaceName,
  onEditSaved,
  onEditControlsChange,
}: HttpAgentFormProps) {
  const styles = useStyles();
  const [displayName, setDisplayName] = useState(agent.displayName ?? '');
  const [description, setDescription] = useState(agent.description ?? '');
  const [baseUrl, setBaseUrl] = useState(spec.init.endpoint.baseUrl ?? '');
  const [timeout, setTimeout] = useState(
    spec.init.endpoint.timeoutSeconds ?? '',
  );
  const [retryAttempts, setRetryAttempts] = useState(
    spec.init.endpoint.retryMaxAttempts ?? '',
  );
  const [retryDelay, setRetryDelay] = useState(
    spec.init.endpoint.retryDelayMs ?? '',
  );
  const [method, setMethod] = useState(spec.invoke?.method ?? 'POST');
  const [path, setPath] = useState(spec.invoke?.path ?? '/act');
  const [body, setBody] = useState(spec.invoke?.body ?? '');
  const [headers, setHeaders] = useState<HeaderRow[]>(() =>
    Object.entries(spec.invoke?.headers ?? {}).map(([name, value]) => ({
      name,
      value,
    })),
  );
  const { replaceAgent, isSaving, error } = useAgentMutations({
    workspaceName,
  });

  const validationError = useMemo(() => {
    if (displayName.length > 100 || description.length > 500) {
      return 'Display name or description exceeds the allowed length.';
    }
    if (
      (!baseUrl.trim() && !spec.init.processStartInfo) ||
      (baseUrl.trim() && !isHttpBaseUrl(baseUrl.trim()))
    ) {
      return 'Enter an HTTP or HTTPS base URL unless a sidecar is configured.';
    }
    if (timeout && (!/^\d+$/.test(timeout) || Number(timeout) < 1)) {
      return 'Timeout must be a positive number of seconds.';
    }
    if (retryAttempts && !/^\d+$/.test(retryAttempts)) {
      return 'Retry attempts must be a non-negative integer.';
    }
    if (retryDelay && !/^\d+$/.test(retryDelay)) {
      return 'Retry delay must be a non-negative integer of milliseconds.';
    }
    if (
      !['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())
    ) {
      return 'Choose a supported HTTP method.';
    }
    if (!path.startsWith('/')) {
      return 'Path must start with /.';
    }
    const names = headers.map(({ name }) => name.trim().toLowerCase());
    if (
      headers.some(
        ({ name }) => !/^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/.test(name.trim()),
      ) ||
      new Set(names).size !== names.length
    ) {
      return 'Header names must be valid and unique.';
    }
    return null;
  }, [
    displayName,
    description,
    baseUrl,
    spec.init.processStartInfo,
    timeout,
    retryAttempts,
    retryDelay,
    method,
    path,
    headers,
  ]);

  const save = useCallback(async () => {
    if (validationError !== null || isSaving) return;
    const headerValues = Object.fromEntries(
      headers.map(({ name, value }) => [name.trim(), value]),
    );
    try {
      await replaceAgent(agent.name, {
        displayName: displayName.trim(),
        description,
        metadata: agent.metadata,
        variables: agent.variables,
        spec: {
          ...spec,
          init: {
            ...spec.init,
            endpoint: {
              ...spec.init.endpoint,
              baseUrl: baseUrl.trim(),
              timeoutSeconds: optional(timeout),
              retryMaxAttempts: optional(retryAttempts),
              retryDelayMs: optional(retryDelay),
            },
          },
          invoke: {
            ...spec.invoke,
            path: path.trim(),
            method: method.toUpperCase(),
            headers: headerValues,
            body,
          },
        },
      });
      onEditSaved();
    } catch {
      // The mutation hook exposes the error in the form.
    }
  }, [
    validationError,
    isSaving,
    headers,
    replaceAgent,
    agent,
    displayName,
    description,
    spec,
    baseUrl,
    timeout,
    retryAttempts,
    retryDelay,
    path,
    method,
    body,
    onEditSaved,
  ]);

  useEffect(() => {
    onEditControlsChange({
      canSave: validationError === null && !isSaving,
      saving: isSaving,
      save: () => void save(),
    });
  }, [onEditControlsChange, validationError, isSaving, save]);
  useEffect(() => () => onEditControlsChange(null), [onEditControlsChange]);

  const updateHeader = (index: number, patch: Partial<HeaderRow>) => {
    setHeaders((current) =>
      current.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.scroll}>
        <div className={styles.cards}>
          <section className={styles.card}>
            <div className={styles.cardLabel}>Identity</div>
            <div className={styles.cardBody}>
              <Field label="Name">
                <Input value={agent.name} disabled />
              </Field>
              <Field label="Display name">
                <Input
                  value={displayName}
                  onChange={(_, data) => setDisplayName(data.value)}
                />
              </Field>
              <Field label="Description">
                <Textarea
                  value={description}
                  onChange={(_, data) => setDescription(data.value)}
                  resize="vertical"
                />
              </Field>
            </div>
          </section>
          <section className={styles.card}>
            <div className={styles.cardLabel}>HTTP endpoint</div>
            <div className={styles.cardBody}>
              <Field label="Base URL" required>
                <Input
                  value={baseUrl}
                  onChange={(_, data) => setBaseUrl(data.value)}
                />
              </Field>
              <Field label="Timeout (seconds)">
                <Input
                  value={timeout}
                  onChange={(_, data) => setTimeout(data.value)}
                />
              </Field>
              <Field label="Retry attempts">
                <Input
                  value={retryAttempts}
                  onChange={(_, data) => setRetryAttempts(data.value)}
                />
              </Field>
              <Field label="Retry delay (ms)">
                <Input
                  value={retryDelay}
                  onChange={(_, data) => setRetryDelay(data.value)}
                />
              </Field>
            </div>
          </section>
          <section className={styles.card}>
            <div className={styles.cardLabel}>HTTP invocation</div>
            <div className={styles.cardBody}>
              <Field label="Method">
                <Input
                  value={method}
                  onChange={(_, data) => setMethod(data.value)}
                />
              </Field>
              <Field label="Path">
                <Input
                  value={path}
                  onChange={(_, data) => setPath(data.value)}
                />
              </Field>
              {headers.map((header, index) => (
                <div className={styles.fieldRow} key={index}>
                  <Field
                    className={styles.fieldColumn}
                    label={`Header name ${index + 1}`}
                  >
                    <Input
                      value={header.name}
                      onChange={(_, data) =>
                        updateHeader(index, { name: data.value })
                      }
                    />
                  </Field>
                  <Field
                    className={styles.fieldColumn}
                    label={`Header value ${index + 1}`}
                  >
                    <Input
                      type={
                        isSensitiveHeader(header.name) ? 'password' : 'text'
                      }
                      value={header.value}
                      onChange={(_, data) =>
                        updateHeader(index, { value: data.value })
                      }
                    />
                  </Field>
                  <Button
                    appearance="subtle"
                    aria-label={`Remove header ${header.name}`}
                    onClick={() =>
                      setHeaders((current) =>
                        current.filter((_, i) => i !== index),
                      )
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                appearance="secondary"
                onClick={() =>
                  setHeaders((current) => [...current, { name: '', value: '' }])
                }
              >
                Add header
              </Button>
              <Field label="Request body">
                <Textarea
                  value={body}
                  onChange={(_, data) => setBody(data.value)}
                  resize="vertical"
                />
              </Field>
            </div>
          </section>
          {validationError && (
            <Body1 role="alert" className={styles.errorText}>
              {validationError}
            </Body1>
          )}
          {error && (
            <Body1 role="alert" className={styles.errorText}>
              {error}
            </Body1>
          )}
        </div>
      </div>
    </div>
  );
}
